import {partition} from '@benchling/js-utils';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {isFullUTFlexibilityEnabled} from 'modbio/util';
import * as FilterUtil from 'search/FilterUtil';
import {Filter, FilterErrors, FilterKey, FullTextFilter, Operator} from 'search/SearchConstants';

// Keep in sync with benchling.sightglass2.models.full_text_ngrams:FullTextDNANgrams.NGRAM_LENGTH
export const MINIMUM_SEQUENCE_LENGTH = 8;
// Keep in sync with benchling.sightglass2.models.full_text_ngrams:FullTextAANgrams.NGRAM_LENGTH
export const MINIMUM_AMINO_ACID_LENGTH = 3;

function getResiduesFilters(
  filters: Array<Filter>,
  residueType: FilterKey.SEQUENCE_BASES | FilterKey.PROTEIN_AMINO_ACIDS
): Array<Filter> {
  return FilterUtil.getFiltersByKey(filters, residueType);
}

export function filterHasMixedBaseValue(filter: FullTextFilter): boolean {
  return Boolean(
    filter.value && filter.value.toUpperCase().includes('T') && filter.value.toUpperCase().includes('U')
  );
}

export default function validateResiduesFilters(filters: Array<Filter>): FilterErrors {
  const sequenceFilters = getResiduesFilters(filters, FilterKey.SEQUENCE_BASES);
  const aminoAcidFilters = getResiduesFilters(filters, FilterKey.PROTEIN_AMINO_ACIDS);
  return filters.map((filter) => {
    if (filter.key === FilterKey.SEQUENCE_BASES) {
      if (sequenceFilters.length > 1 && filter !== sequenceFilters[0]) {
        return 'Only one nucleotide string filter can be applied at a time';
      } else if (
        filter.value &&
        filter.operator === Operator.INCLUDES_TEXT &&
        filter.value.length < MINIMUM_SEQUENCE_LENGTH
      ) {
        return 'Nucleotide string search requires at least 8 bases';
      } else if (!filter.value && filter.operator !== Operator.IS_EMPTY) {
        return 'Nucleotide string cannot be empty. Use "is empty" to specify an empty sequence.';
      } else if (
        filter.operator !== Operator.IS_EMPTY &&
        isFullUTFlexibilityEnabled() &&
        filterHasMixedBaseValue(filter)
      ) {
        const typesFilter = FilterUtil.getFilterByKey(filters, FilterKey.TYPES);
        const types = typesFilter ? (typesFilter.value as Array<string>) : [];
        if (types.includes(ModelTypeConstants.SEQUENCE) || types.includes(ModelTypeConstants.RNA_SEQUENCE)) {
          return 'Mixed bases queries are only supported for oligos.';
        }
      }
    } else if (filter.key === FilterKey.PROTEIN_AMINO_ACIDS) {
      if (aminoAcidFilters.length > 1 && filter !== aminoAcidFilters[0]) {
        return 'Only one AA filter can be applied at a time';
      } else if (filter.value && filter.value.length < MINIMUM_AMINO_ACID_LENGTH) {
        return 'AA search requires at least 3 residues';
      }
    }
    return null;
  });
}

export function partitionTypesForSequenceBasesFilter(
  filter: FullTextFilter,
  currentTypes: Array<string>
): [Array<string>, Array<string>] {
  if (isFullUTFlexibilityEnabled() && filterHasMixedBaseValue(filter)) {
    return partition(
      currentTypes,
      (t) => t !== ModelTypeConstants.SEQUENCE && t !== ModelTypeConstants.RNA_SEQUENCE
    );
  }

  return [currentTypes, []];
}
